import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { uniqueDivisionKeys } from '../../lib/divisions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };
const LEVELS = { field: 'Field Tech', office: 'Office', manager: 'Manager', admin: 'Admin' };
const NAV_LABELS = { office_work: 'Office Work', schedule: 'Schedule', customers: 'Customers', field_tools: 'Field Tools', workforce: 'Workforce', billing: 'Billing', inventory: 'Inventory', admin_system: 'System / Admin' };
const sel = 'w-full h-9 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-2';

const ScopeEditor = ({ label, value, items, onChange, testid }) => {
  const mode = Array.isArray(value) ? 'list' : value;
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">{label}</p>
      <select data-testid={`${testid}-mode`} value={mode} onChange={(e) => onChange(e.target.value === 'list' ? [] : e.target.value)} className={sel}>
        <option value="all">All</option>
        <option value="primary">Employee's primary only</option>
        <option value="list">Specific…</option>
      </select>
      {mode === 'list' && (
        <div className="flex flex-wrap gap-1.5">
          {items.map((it) => {
            const on = value.includes(it.id);
            return <button type="button" key={it.id} data-testid={`${testid}-opt-${it.id}`} onClick={() => onChange(on ? value.filter((x) => x !== it.id) : [...value, it.id])} className={`text-[11px] px-2 py-0.5 rounded-full border ${on ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]' : 'border-[#3f3f3f] text-[#A3A3A3]'}`}>{it.name}</button>;
          })}
        </div>
      )}
    </div>
  );
};

const RoleEditor = ({ role, meta, roles, onClose, onSaved }) => {
  const [f, setF] = useState(role);
  const [saving, setSaving] = useState(false);
  useEffect(() => setF(role), [role]);
  if (!f) return null;
  const isNew = !f.id;
  const save = async () => {
    setSaving(true);
    try {
      const body = { name: f.name, scope: f.scope, landing_path: f.landing_path, nav_groups: f.nav_groups, sort_order: f.sort_order };
      if (!f.is_system) body.base_level = f.base_level;
      if (f.uses_default_permissions) body.use_default_permissions = true; else body.permissions = f.permissions;
      if (isNew) await axios.post(`${API}/admin/roles`, body, opts); else await axios.put(`${API}/admin/roles/${f.id}`, body, opts);
      toast.success(isNew ? 'Role created' : 'Role saved'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };
  const LEGACY = { field: 'technician', office: 'office_staff', manager: 'manager', admin: 'admin' };
  const levelDefaults = () => (roles.find((r) => r.is_system && r.key === LEGACY[f.base_level])?.permissions || []);
  const shown = f.uses_default_permissions ? (f.id ? f.permissions : levelDefaults()) : f.permissions;
  const isAdminLevel = f.base_level === 'admin';
  const togglePerm = (p) => setF({ ...f, uses_default_permissions: false, permissions: shown.includes(p) ? shown.filter((x) => x !== p) : [...shown, p] });
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="role-editor" className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-white">{isNew ? 'New Role' : `Edit Role — ${role.name}`}</SheetTitle>
          <SheetDescription className="text-[#A3A3A3]">Permissions decide what this role can do; scope decides where it applies. The role name never controls access.</SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <div><p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Name</p><Input data-testid="role-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
          <div>
            <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Base access level <span className="normal-case text-[#525252]">(legacy compatibility bridge)</span></p>
            <select data-testid="role-base-level" disabled={f.is_system} value={f.base_level} onChange={(e) => setF({ ...f, base_level: e.target.value })} className={sel}>
              {Object.entries(LEVELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            {f.is_system && <p className="text-[10px] text-[#525252] mt-1">System role — base level is fixed.</p>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ScopeEditor testid="role-scope-trades" label="Allowed Trades" value={f.scope.departments} items={meta.departments} onChange={(v) => setF({ ...f, scope: { ...f.scope, departments: v } })} />
            <ScopeEditor testid="role-scope-divisions" label="Allowed Divisions" value={f.scope.divisions} items={uniqueDivisionKeys(meta.divisions).map((d) => ({ id: d.key, name: d.name }))} onChange={(v) => setF({ ...f, scope: { ...f.scope, divisions: v } })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Default landing page</p><Input data-testid="role-landing" value={f.landing_path} onChange={(e) => setF({ ...f, landing_path: e.target.value })} placeholder="/admin or /employee" className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
            <div>
              <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Navigation groups</p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {meta.nav_groups.map((g) => {
                  const on = f.nav_groups.includes(g);
                  return <button type="button" key={g} data-testid={`role-nav-${g}`} onClick={() => setF({ ...f, nav_groups: on ? f.nav_groups.filter((x) => x !== g) : [...f.nav_groups, g] })} className={`text-[11px] px-2 py-0.5 rounded-full border ${on ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]' : 'border-[#3f3f3f] text-[#A3A3A3]'}`}>{NAV_LABELS[g] || g}</button>;
                })}
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Permissions ({isAdminLevel ? 'all' : shown.length})</p>
              <label className="text-[11px] text-[#A3A3A3] flex items-center gap-1.5"><input data-testid="role-use-defaults" type="checkbox" disabled={isAdminLevel} checked={!!f.uses_default_permissions} onChange={(e) => setF({ ...f, uses_default_permissions: e.target.checked, permissions: e.target.checked ? f.permissions : shown })} /> Use base-level defaults</label>
            </div>
            {isAdminLevel && <p data-testid="role-admin-note" className="text-[11px] text-orange-300 mt-1">Admin-level roles always have full access; permissions can't be narrowed. Use Manager or Office level for a restricted role.</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 mt-2 max-h-72 overflow-y-auto border border-[#262626] rounded p-2">
              {meta.all_permissions.map((p) => (
                <label key={p} className="text-[11px] text-[#D4D4D4] flex items-center gap-1.5"><input data-testid={`role-perm-${p}`} type="checkbox" disabled={isAdminLevel} checked={isAdminLevel || shown.includes(p)} onChange={() => togglePerm(p)} /> {p.replace(/_/g, ' ')}</label>
              ))}
            </div>
          </div>
          <Button data-testid="role-save" disabled={saving} onClick={save} className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold">{saving ? 'Saving…' : (isNew ? 'Create Role' : 'Save Role')}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default function AdminRoles() {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    try {
      const [r, d, v] = await Promise.all([axios.get(`${API}/admin/roles`, opts), axios.get(`${API}/inv/departments`, opts), axios.get(`${API}/divisions`, opts)]);
      setData(r.data); setMeta({ departments: d.data, divisions: v.data, all_permissions: r.data.all_permissions, nav_groups: r.data.nav_groups });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load roles'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (r) => {
    try { await axios.put(`${API}/admin/roles/${r.id}`, { active: !r.active }, opts); toast.success(r.active ? 'Deactivated' : 'Reactivated'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const scopeTxt = (v, list, key) => (v === 'all' ? 'All' : v === 'primary' ? 'Primary' : v.map((x) => list.find((i) => i[key] === x)?.name || x).join(', '));
  if (!data || !meta) return <p className="text-[#A3A3A3]">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="roles-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-[#D4AF37]" /> Role Manager</h2>
          <p className="text-[#A3A3A3] text-sm max-w-3xl">Reusable roles: default permissions, where they apply (Trades + Divisions), and default workspace. Per-employee permission overrides still live in Roles &amp; Permissions.</p>
        </div>
        <Button data-testid="role-new" onClick={() => setEditing({ name: '', base_level: 'field', scope: { departments: 'primary', divisions: 'primary' }, landing_path: '', nav_groups: [], permissions: [], uses_default_permissions: true, sort_order: data.roles.length })} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-1" /> New Role</Button>
      </div>
      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="roles-table">
          <thead className="text-[10px] uppercase tracking-wider text-[#A3A3A3]"><tr>{['Role', 'Base level', 'Trades', 'Divisions', 'Permissions', 'Employees', ''].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
          <tbody>
            {data.roles.map((r) => (
              <tr key={r.id} data-testid={`role-row-${r.id}`} className={`border-t border-[#262626] ${r.active ? '' : 'opacity-50'}`}>
                <td className="px-3 py-2 text-white font-semibold">{r.name} {r.is_system && <span className="ml-1 text-[9px] uppercase border border-[#3f3f3f] text-[#A3A3A3] rounded px-1">System</span>}</td>
                <td className="px-3 py-2 text-[#D4D4D4]">{LEVELS[r.base_level]}</td>
                <td className="px-3 py-2 text-[#D4D4D4]" data-testid={`role-trades-${r.id}`}>{scopeTxt(r.scope.departments, meta.departments, 'id')}</td>
                <td className="px-3 py-2 text-[#D4D4D4]" data-testid={`role-divisions-${r.id}`}>{scopeTxt(r.scope.divisions, meta.divisions, 'key')}</td>
                <td className="px-3 py-2 text-[#A3A3A3] text-xs">{r.uses_default_permissions ? 'Defaults' : 'Custom'} ({r.permissions.length})</td>
                <td className="px-3 py-2 text-[#D4D4D4]">{r.employee_count}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button data-testid={`role-edit-${r.id}`} onClick={() => setEditing(r)} className="text-xs text-[#D4AF37] hover:underline mr-3">Edit</button>
                  <button data-testid={`role-toggle-${r.id}`} onClick={() => toggle(r)} className="text-xs text-[#A3A3A3] hover:text-white">{r.active ? 'Deactivate' : 'Reactivate'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <RoleEditor role={editing} meta={meta} roles={data.roles} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
